<script setup lang="ts">
import { onMounted, computed, onUnmounted } from "vue";
import Article from "~/components/card/Article.vue";
import { useHomeRecommendCategoryCollectionStore } from "~/stores/home/homeRecommendCategoryCollection";
import { useArticleDetailStore } from "~/stores/article/articleDetail";
import { useRoute } from "vue-router";
import { useLazyAsyncData } from "#app";
import { ContentTypeEnum } from "~/types/enum/ContentTypeEnum";
import { dateDayFormatter } from "~/utils/formatters/date.formatter";
import { clearAdsenseBlocks } from "~/utils/adsense";

const articleDetailStore = useArticleDetailStore();
const homeRecommendCategoryCollectionStore = useHomeRecommendCategoryCollectionStore();
const slug = String(useRoute().params?.slug);

homeRecommendCategoryCollectionStore.fetchList();
await useLazyAsyncData('articleDetailStore', async () => articleDetailStore.fetch(slug));
if (typeof articleDetailStore.error === "object") {
    throw articleDetailStore.error;
}

const recommendCategoryCollection = computed(() => homeRecommendCategoryCollectionStore.list);

onMounted(() => {
    window.scrollTo(0, 0);
});

onUnmounted(() => {
    clearAdsenseBlocks();
    articleDetailStore.clear();
});
</script>

<template>
    <PartFancybox only-js-lib />
    <article class="max-w-2xl px-6 mx-auto">
        <div class="w-full mx-auto space-y-4">
            <h1 v-if="articleDetailStore.title" class="text-4xl font-bold tracking-tight md:text-5xl xl:text-6xl">{{ articleDetailStore.title }}</h1>
            <h1 v-else-if="articleDetailStore.title === undefined" class="text-4xl font-bold tracking-tight md:text-5xl xl:text-6xl">
                <span class="animate-pulse rounded block h-10 w-[80%] bg-gray-100"></span>
                <span class="animate-pulse rounded block h-10 w-[95%] mt-3 bg-gray-100"></span>
                <span class="animate-pulse rounded block h-10 w-[60%] mt-3 bg-gray-100"></span>
            </h1>
            <div v-if="articleDetailStore.title">
                <nuxt-link to="/interesting-facts" class="font-semibold text-indigo-800 mr-1">
                    Interesting Facts <span aria-hidden="true">&rarr;</span>
                </nuxt-link>
                <nuxt-link :to="articleDetailStore.category.url" class="font-semibold text-indigo-800">
                    {{ articleDetailStore.category.title }}
                </nuxt-link>
            </div>
            <div v-else-if="articleDetailStore.title === undefined">
                <span class="animate-pulse rounded block h-5 w-20 bg-indigo-50"></span>
            </div>
            <p v-if="articleDetailStore.title" class="text-sm">
                <time :datetime="articleDetailStore.date">{{ dateDayFormatter(articleDetailStore.date) }}</time>
            </p>
            <p v-else-if="articleDetailStore.title === undefined" class="text-sm">
                <span class="animate-pulse rounded block h-5 w-20 bg-gray-100"></span>
            </p>
        </div>
    </article>
    <template v-if="articleDetailStore.content !== undefined && articleDetailStore.content.length"
              v-for="content in articleDetailStore.content"
    >
        <ClientOnly>
            <article class="container max-w-2xl px-6 mx-auto content-text flex justify-center">
                <AdsenseArticleCard class="w-full"/>
            </article>
        </ClientOnly>
        <ContentText v-if="content.type === ContentTypeEnum.Text" :content="content"/>
        <ContentRandomRecipesByCategory v-if="content.type === ContentTypeEnum.RandomRecipesByCategory" :content="content"/>
        <ContentImageText v-if="content.type === ContentTypeEnum.ImageText" :content="content" :title="articleDetailStore.title"/>
    </template>
    <template v-if="articleDetailStore.content === undefined">
        <article v-for="index in 3" :key="index" class="max-w-2xl px-6 mx-auto space-y-4 prose pt-10">
            <span class="animate-pulse rounded block h-5 w-[80%] bg-gray-100"></span>
            <span class="animate-pulse rounded block h-5 w-[95%] bg-gray-100"></span>
            <span class="animate-pulse rounded block h-5 w-[60%] bg-gray-100"></span>
            <span class="animate-pulse rounded block h-5 w-[80%] bg-gray-100"></span>
            <span class="animate-pulse rounded block h-5 w-[95%] bg-gray-100"></span>
            <span class="animate-pulse rounded block h-5 w-[60%] bg-gray-100"></span>
        </article>
    </template>

    <ClientOnly>
        <section class="container flex justify-center">
            <AdsenseMedia />
        </section>
    </ClientOnly>
    <section class="container grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <LastInterestingFactsByCategoryToAside :category-id="2" :page-size="4"/>
        <LastInterestingFactsByCategoryToAside :category-id="4" :page-size="4"/>
        <LastInterestingFactsByCategoryToAside :category-id="3" :page-size="4"/>
        <LastInterestingFactsByCategoryToAside :category-id="1" :page-size="4"/>
    </section>
    <RecommendCategoryCollection v-if="recommendCategoryCollection !== undefined" :items="recommendCategoryCollection" />
    <RecommendCategoryCollection v-else skeleton />
</template>